import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import Button from '../../components/ui/Button';
import Tooltip from '../../components/ui/Tooltip';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import DecisionRadio from '../period-goals/DecisionRadio';
import { useAuth } from '../../context/AuthContext';
import { ANONYMOUS_EVALUATOR_ROLES, usePerformanceAiApi } from './config';
import PerformanceContextPanel from '../performance-context/PerformanceContextPanel';
import { canShowAiDraft, canShowContextPanel } from './fillFeedbackGates';
import AiDraftSources from './AiDraftSources';
import useActiveEditTimer from './useActiveEditTimer';

// Task 28 — form doldurma modalı: GET v1/feedback-responses/my/{id}/form
// üzerinden sorular (SortOrder sıralı) + bağlam (değerlendirilen adı/rolü/dönem
// adı) + varsa önceki cevaplar (yeniden gönderim ön-doldurma). Likert
// sorularında DecisionRadio ORTAK KULLANILIYOR (period-goals/DecisionRadio.js —
// kopyalanmadı, doğrudan içe aktarıldı; bileşen tamamen jenerik: name/options/
// value/onChange, 1-5 ölçeğine de sorunsuz uyuyor). YÖNETİCİ rolünde zorunlu
// "Genel Performans Seviyesi" — FillAsync bu alanı YALNIZ manager rolünde
// zorunlu kılıyor, diğer rollerde gönderilirse "overallLevelForbidden" ile
// reddediyor (bu yüzden payload'da yalnız manager iken eklenir).
const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1400;
  padding: 24px;
  overflow-y: auto;
`;

// P2 Faz 1: yönetici doldururken sağda bağlam paneli açılır (iki kolon) —
// CalibrationDecisionModal ile aynı düzen; 980px altında panel alta düşer.
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $withPanel }) => ($withPanel ? 'minmax(0, 1fr) 320px' : 'minmax(0, 1fr)')};
  gap: 20px;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const MainColumn = styled.div`
  min-width: 0;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: ${({ $withPanel }) => ($withPanel ? '1040px' : '680px')};
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
`;

const DialogTitle = styled.h3`
  margin: 0 0 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SubTitle = styled.div`
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 16px;
`;

const AnonymityStrip = styled.div`
  background: #eff6ff;
  color: #0291ef;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 12px;
  margin-bottom: 18px;
`;

// AI taslağı şeritleri — AnonymityStrip'in aynı görsel dili (aynı yarıçap/dolgu/
// punto); bilgi mavi, kanıt-zayıf uyarısı kehribar.
const AiBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

const AiStrip = styled.div`
  background: #eff6ff;
  color: #0291ef;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 12px;
  margin-bottom: 10px;
`;

const ThinEvidenceStrip = styled.div`
  background: #fff7ed;
  color: #b45309;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 12px;
  margin-bottom: 18px;
`;

const QuestionBlock = styled.div`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e8ecf0;
`;

const QuestionText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 10px;
`;

const ScaleHint = styled.div`
  font-size: 11px;
  color: #6c757d;
  margin-top: 6px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 20px 0 10px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const EmptyBox = styled.div`
  padding: 20px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

const LIKERT_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }));

// P1 — GET my/{id}/form cevabındaki likertAnchors (tenant tanımlı 5 çapa
// etiketi; PerformanceSettings.LikertAnchorLabelsJson). BE geçersiz/boş tanımda
// null döner → mevcut varsayılan ölçek metni (likertScaleHint) kullanılır.
const hasAnchors = (anchors) => Array.isArray(anchors) && anchors.length === 5;

const buildLikertOptions = (anchors) => (hasAnchors(anchors)
  ? LIKERT_OPTIONS.map((opt, i) => ({ ...opt, title: `${opt.value} — ${anchors[i]}` }))
  : LIKERT_OPTIONS);

const anchorsLegend = (anchors) => anchors.map((a, i) => `${i + 1} — ${a}`).join(' · ');

export default function FillFeedbackModal({ open, responseId, onClose, onFilled, getForm, submitFill }) {
  const { t } = useTranslation();
  const { hasModule } = useAuth();
  // Uç bu bileşenin KENDİ bağımlılığı (FillFeedbackPage'e yeni prop eklenmedi) —
  // getForm/submitFill prop'ları Task 28'den kalan mevcut sözleşmedir.
  const { draftFeedback, recordOutcome, recordPrepSession } = usePerformanceAiApi();
  // P7a — kaynaklı taslak (ifadeler + kaynaklar) ve M72/M73 ölçümü: aktif düzenleme süresi + taslağın aynen/düzenlenerek
  // kaydedilip kaydedilmediği. Yazılan metinler karşılaştırma için tutulur (sunucuya gitmez).
  const [aiDraft, setAiDraft] = useState(null);
  const timer = useActiveEditTimer(open);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [overallLevelId, setOverallLevelId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Yüklenen (ön-doldurulmuş) cevapların anlık görüntüsü — İptal'de "girilen
  // cevap var mı" (dirty) ayrımı bu görüntüyle yapılır; hiç dokunulmamış
  // yeniden-gönderim formunda gereksiz onay sorulmaz.
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftShown, setDraftShown] = useState(false);
  const [draftThinEvidence, setDraftThinEvidence] = useState(false);

  useEffect(() => {
    if (!open || !responseId) { setForm(null); return; }
    setLoading(true);
    setCloseConfirmOpen(false);
    setDraftShown(false);
    setDraftThinEvidence(false);
    setAiDraft(null);
    getForm(responseId).then((data) => {
      setForm(data);
      const initialAnswers = {};
      (data?.existingAnswers || []).forEach((a) => {
        initialAnswers[a.questionId] = { likertValue: a.likertValue, textValue: a.textValue };
      });
      setAnswers(initialAnswers);
      setOverallLevelId(data?.overallLevelId || null);
      setInitialSnapshot(JSON.stringify({ answers: initialAnswers, overallLevelId: data?.overallLevelId || null }));
    }).finally(() => setLoading(false));
  }, [open, responseId, getForm]);

  const questions = useMemo(() => {
    if (!form?.questions) return [];
    return [...form.questions].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [form]);

  const likertOptions = useMemo(() => buildLikertOptions(form?.likertAnchors), [form?.likertAnchors]);

  const isAnonymousRole = form && ANONYMOUS_EVALUATOR_ROLES.includes(form.evaluatorRole);
  const isManagerRole = form?.evaluatorRole === 'manager';

  // Plan 2 (Task 8) — yalnız manager rolünde dolu (FeedbackFormForFillResponse
  // .OverallLevelOptions, dönemin skalasından, SortOrder sıralı); sabit üç
  // token'lı liste kalktı.
  const overallLevelOptions = useMemo(() => (
    [...(form?.overallLevelOptions || [])].sort((a, b) => a.sortOrder - b.sortOrder)
  ), [form]);

  const setLikertValue = useCallback((questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { ...prev[questionId], likertValue: value } }));
  }, []);

  const setTextValue = useCallback((questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { ...prev[questionId], textValue: value } }));
  }, []);

  const openTextQuestions = useMemo(
    () => questions.filter((q) => q.questionType === 'open_text'),
    [questions],
  );

  // Görünürlük kuralları fillFeedbackGates.js'te (saf + testli): yönetici
  // formu + AI Plus aboneliği + en az bir açık uçlu soru. Faz 3 / PM-07 —
  // PM ayrıcalık koşulu KALKTI (gerekçe fillFeedbackGates.js'te): uçlar artık
  // [Authorize]-only, kapsam/abonelik serviste. Öz değerlendirme formunda
  // düğme YOK (müşteri geri bildirimi 2026-10-28).
  const canDraft = canShowAiDraft({
    isManagerRole,
    hasAiModule: hasModule('AI'),
    openTextCount: openTextQuestions.length,
  });

  // Bağlam paneli yalnız yönetici formunda anlamlı; kapsam (İK ∪ doğrudan
  // rapor) BE'de doğrulanır.
  const showContextPanel = canShowContextPanel({ isManagerRole });

  // Dönen 4 alan open_text sorularına SIRAYLA yazılır. Soru sayısı 4'ten azsa
  // artan alanlar son soruda genel yorumla birleştirilir (hiçbir alan
  // kaybolmaz); 4'ten fazlaysa kalan sorular BOŞ kalır (yönetici kendi
  // yazsın). OverallLevel'a ASLA dokunulmaz — kategorik yargı insanda kalır.
  const handleDraft = useCallback(async () => {
    if (!form?.periodId || !form?.employeeId || drafting) return;
    setDrafting(true);
    const draft = await draftFeedback(form.periodId, form.employeeId);
    setDrafting(false);
    // Hata yolunda (null) form verisi korunur; toast'ı Axios interceptor gösterir.
    if (!draft) return;

    const values = [draft.strengths, draft.developmentAreas, draft.notableAchievement, draft.generalComment]
      .map((v) => (v || '').trim());

    const written = {};
    openTextQuestions.forEach((q, i) => {
      if (i >= values.length) return;
      const isLastTarget = i === openTextQuestions.length - 1;
      const text = (isLastTarget && openTextQuestions.length < values.length)
        ? values.slice(i).filter(Boolean).join('\n\n')
        : values[i];
      setTextValue(q.id, text);
      written[q.id] = text;
    });

    setDraftShown(true);
    setDraftThinEvidence(Boolean(draft.evidenceThin));
    setAiDraft({ ...draft, written });
  }, [form, drafting, draftFeedback, openTextQuestions, setTextValue]);

  const allLikertAnswered = questions
    .filter((q) => q.questionType === 'likert')
    .every((q) => {
      const v = answers[q.id]?.likertValue;
      return v >= 1 && v <= 5;
    });
  const overallLevelOk = !isManagerRole || !!overallLevelId;
  const canSubmit = questions.length > 0 && allLikertAnswered && overallLevelOk;

  const handleSubmit = useCallback(async () => {
    if (!responseId || !canSubmit) return;
    setSubmitting(true);
    const payload = {
      answers: questions
        .map((q) => {
          const a = answers[q.id] || {};
          if (q.questionType === 'likert' && a.likertValue) {
            return { questionId: q.id, likertValue: a.likertValue };
          }
          if (q.questionType === 'open_text' && a.textValue && a.textValue.trim()) {
            return { questionId: q.id, textValue: a.textValue.trim() };
          }
          return null;
        })
        .filter(Boolean),
      overallLevelId: isManagerRole ? overallLevelId : undefined,
    };
    const ok = await submitFill(responseId, payload);
    setSubmitting(false);
    if (ok) {
      // P7a (M72/M73) — taslak kaydedildi mi (aynen / düzenlenerek) ya da AI'sız doldurulan formun hazırlık süresi.
      // Baz süre yalnız AI taslağı SUNULAN formda (yönetici) anlamlı — öz değerlendirmede taslak yok, süre de tutulmaz.
      const seconds = timer.seconds();
      if (aiDraft?.draftId) {
        const edited = Object.entries(aiDraft.written || {}).some(([qid, text]) => (answers[qid]?.textValue || '').trim() !== (text || '').trim());
        recordOutcome(aiDraft.draftId, edited ? 'saved_edited' : 'saved_as_is', seconds);
      } else if (isManagerRole && openTextQuestions.length > 0) {
        recordPrepSession('manager_feedback', form?.periodId, seconds);
      }
      onFilled();
    }
  }, [responseId, canSubmit, questions, answers, isManagerRole, overallLevelId, submitFill, onFilled,
    timer, aiDraft, recordOutcome, recordPrepSession, openTextQuestions.length, form?.periodId]);

  // Likert + metin cevapları tek yanlış İptal tıklamasıyla kaybolmasın:
  // yüklenen anlık görüntüden sapma varsa kapatmadan önce onay istenir
  // (PeriodWizard'ın requestClose deseninin aynısı, ortak common.* anahtarları).
  const isDirty = initialSnapshot != null
    && JSON.stringify({ answers, overallLevelId }) !== initialSnapshot;

  // P7a (M72) — üretilen taslak kaydedilmeden kapatılırsa "vazgeçildi".
  const closeAndRecord = useCallback(() => {
    if (aiDraft?.draftId) recordOutcome(aiDraft.draftId, 'discarded', timer.seconds());
    onClose();
  }, [aiDraft, recordOutcome, timer, onClose]);

  const requestClose = useCallback(() => {
    if (isDirty) {
      setCloseConfirmOpen(true);
      return;
    }
    closeAndRecord();
  }, [isDirty, closeAndRecord]);

  if (!open) return null;

  return (
    <Modal>
      <ModalCard $withPanel={showContextPanel} onKeyDownCapture={timer.touch} onInputCapture={timer.touch} onClickCapture={timer.touch}>
        <ConfirmDialog
          isOpen={closeConfirmOpen}
          title={t('common.unsavedChangesTitle')}
          message={t('common.unsavedChangesConfirm')}
          onConfirm={closeAndRecord}
          onCancel={() => setCloseConfirmOpen(false)}
        />
        {loading && <EmptyBox>{t('common.loading')}</EmptyBox>}

        {!loading && !form && <EmptyBox>{t('performance.feedback360.fillForm.notFound')}</EmptyBox>}

        {!loading && form && (
          <ContentGrid $withPanel={showContextPanel}>
            <MainColumn>
            <DialogTitle>{form.employeeName}</DialogTitle>
            <SubTitle>
              {t(`performance.feedback360.role.${form.evaluatorRole}`, { defaultValue: form.evaluatorRole })}
              {' · '}
              {form.periodName}
            </SubTitle>

            {isAnonymousRole && (
              <AnonymityStrip>{t('performance.feedback360.anonymityStrip')}</AnonymityStrip>
            )}

            {questions.map((q) => (
              <QuestionBlock key={q.id}>
                <QuestionText>{q.text}</QuestionText>
                {q.questionType === 'likert' ? (
                  <>
                    <DecisionRadio
                      name={`likert-${q.id}`}
                      value={answers[q.id]?.likertValue || null}
                      onChange={(v) => setLikertValue(q.id, v)}
                      options={likertOptions}
                    />
                    <ScaleHint>
                      {hasAnchors(form.likertAnchors)
                        ? anchorsLegend(form.likertAnchors)
                        : t('performance.feedback360.fillForm.likertScaleHint')}
                    </ScaleHint>
                  </>
                ) : (
                  <TextInput
                    type="textarea"
                    rows={3}
                    value={answers[q.id]?.textValue || ''}
                    onChange={(e) => setTextValue(q.id, e.target.value)}
                  />
                )}
              </QuestionBlock>
            ))}

            {/* #75 — AI taslak butonu sorulardan ÖNCE, likert cevaplarından
                bağımsız olarak her zaman tıklanabilir duruyordu (kanıt
                zayıflığı likert cevaplarının aksine metin alanlarına
                bakmıyor, ama akış olarak "önce cevapla, sonra taslak
                iste" beklenen sıra). Buton artık soruların EN ALTINA (açık
                uçlu alanlara en yakın konum) taşındı ve TÜM likert sorular
                cevaplanana kadar disabled — ipucu Tooltip ile gösterilir
                (repo'nun standart Tooltip bileşeni, period-goals/config
                weight-cell hücresiyle AYNI kullanım deseni). Banner'lar
                (draftShown/draftThinEvidence) da butonla AYNI konuma
                taşındı — kullanıcı taslağı tetiklediği yerde sonucu görsün,
                sayfanın başına geri kaydırmasın. */}
            {canDraft && (
              <>
                <AiBar>
                  <Tooltip text={allLikertAnswered ? '' : t('performance.ai.draftDisabledHint')}>
                    <Button variant="outline" onClick={handleDraft} disabled={drafting || !allLikertAnswered}>
                      {drafting ? t('performance.ai.generating') : t('performance.ai.draftButton')}
                    </Button>
                  </Tooltip>
                </AiBar>
                {draftShown && <AiStrip>{t('performance.ai.draftBanner')}</AiStrip>}
                {draftShown && aiDraft ? <AiDraftSources draft={aiDraft} /> : null}
                {draftShown && draftThinEvidence && (
                  <ThinEvidenceStrip>{t('performance.ai.thinEvidence')}</ThinEvidenceStrip>
                )}
              </>
            )}

            {isManagerRole && (
              <>
                <SectionTitle>{t('performance.feedback360.fillForm.overallLevel')}</SectionTitle>
                <GenericSelect
                  label={t('performance.feedback360.fillForm.overallLevel')}
                  value={overallLevelId}
                  onChange={setOverallLevelId}
                  options={overallLevelOptions}
                  idKey="id"
                  labelKey="label"
                  required
                />
              </>
            )}
            </MainColumn>

            {/* Panel yalnız yönetici değerlendirmesinde: bağlam ucu PM.Read +
                yönetici kapsamı ister; eşdüzey/ast/öz değerlendirici 403 alır. */}
            {showContextPanel && (
              <PerformanceContextPanel periodId={form.periodId} employeeId={form.employeeId} />
            )}
          </ContentGrid>
        )}

        <Actions>
          <Button variant="outline" onClick={requestClose}>{t('common.cancel')}</Button>
          {!loading && form && (
            <Button onClick={handleSubmit} disabled={!canSubmit || submitting} loading={submitting}>
              {t('performance.feedback360.fillForm.submit')}
            </Button>
          )}
        </Actions>
      </ModalCard>
    </Modal>
  );
}
